import { AuthProvider, useAuth } from '@/auth/AuthContext';
import { Routes, Route }         from 'react-router-dom';
import { useSheetData }          from '@/hooks/useSheetData';
import { TenantProvider }        from '@/contexts/TenantContext';
import { AppShell }              from '@/components/layout/AppShell';
import { LoginPage }             from '@/pages/LoginPage';
import { CortexLoadingScreen }   from '@/components/ui/CortexLoadingScreen';
import { useMemo }               from 'react';

// ─── Page imports ─────────────────────────────────────────────────────────────
import { Dashboard }   from '@/pages/Dashboard';
import { AnomalyPage } from '@/pages/Anomaly';
import { EfficiencyPage } from '@/pages/Efficiency';
import { BillingPage as Billings } from '@/pages/Billings';
import { Alerts }      from '@/pages/Alerts';
import { Settings }    from '@/pages/Settings';

// ─── Authenticated shell ───────────────────────────────────────────────────────

function AuthenticatedApp() {
  const { selectedSheet } = useAuth();
  const data = useSheetData();

  // ── Last DATA timestamp (not fetch time) ──────────────────────────────────
  // The header must show when the SHEET last produced a reading, frozen at that
  // moment — NOT when the app last polled. rows are sorted chronologically by
  // derivedEngine, so the max timestamp is the newest reading in the sheet.
  const lastDataAt = useMemo(() => {
    if (!data.rows.length) return null;
    let maxTs = 0;
    for (const r of data.rows) {
      const t = r.timestamp instanceof Date ? r.timestamp.getTime() : 0;
      if (t > maxTs) maxTs = t;
    }
    return maxTs ? new Date(maxTs) : null;
  }, [data.rows]);

  // Guard: selectedSheet is required by TenantProvider.
  if (!selectedSheet) return null;

  // Show full screen Cortex loading screen on initial data load
  if (data.status === 'loading' && !data.rows.length) {
    return <CortexLoadingScreen fullScreen={true} />;
  }


  const refreshing = data.status === 'loading' || data.status === 'refreshing';


  return (
    <TenantProvider sheet={selectedSheet}>
      <AppShell
        siteName={selectedSheet.displayName ?? selectedSheet.label}
        lastDataAt={lastDataAt}
        refreshing={refreshing}
        onRefresh={data.refetch}
      >
        {/* ── Router wired here — was missing before (was {null}) ── */}
        <Routes>
          <Route path="/"         element={<Dashboard  data={data} />} />
          <Route path="/anomaly"  element={<AnomalyPage data={data} />} />
          <Route path="/efficiency" element={<EfficiencyPage data={data} />} />
          <Route path="/billing"  element={<Billings   data={data} />} />
          <Route path="/alerts"   element={<Alerts     data={data} />} />
          <Route path="/settings" element={<Settings   data={data} />} />
        </Routes>
      </AppShell>
    </TenantProvider>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <AppRoot />
    </AuthProvider>
  );
}

function AppRoot() {
  const { user } = useAuth();

  if (!user) return <LoginPage />;
  return <AuthenticatedApp />;
}